/**
 * Schemas de validação Zod para formulários críticos da aplicação.
 * Use sempre no frontend ANTES de enviar dados para a API.
 */
import { z } from "zod";

/* ─── Helpers reutilizáveis ─── */

const trimmedString = z.string().trim();

const emailSchema = trimmedString
  .min(1, "E-mail é obrigatório")
  .email("E-mail inválido")
  .max(254, "E-mail muito longo");

const telefoneSchema = trimmedString
  .regex(
    /^\(\d{2}\) \d{5}-\d{4}$|^\(\d{2}\) \d{4}-\d{4}$/,
    "Telefone inválido. Use o formato (99) 99999-9999"
  )
  .optional()
  .or(z.literal(""));

const nomeSchema = trimmedString
  .min(2, "Nome deve ter pelo menos 2 caracteres")
  .max(120, "Nome deve ter no máximo 120 caracteres")
  .regex(/^[a-zA-ZÀ-ÿ\s'-]+$/, "Nome contém caracteres inválidos");

const senhaSchema = trimmedString
  .min(8, "Senha deve ter pelo menos 8 caracteres")
  .max(128, "Senha deve ter no máximo 128 caracteres")
  .regex(/[A-Z]/, "Senha deve conter pelo menos uma letra maiúscula")
  .regex(/[a-z]/, "Senha deve conter pelo menos uma letra minúscula")
  .regex(/\d/, "Senha deve conter pelo menos um número")
  .regex(/[^a-zA-Z0-9]/, "Senha deve conter pelo menos um caractere especial");

/* ─── Autenticação ─── */

export const loginSchema = z.object({
  email: emailSchema,
  password: senhaSchema,
});

export const redefinirSenhaSchema = z.object({
  email: emailSchema,
  codigo: trimmedString.min(1, "Código é obrigatório"),
  senha: senhaSchema,
  confirmacaoSenha: trimmedString,
}).refine((data) => data.senha === data.confirmacaoSenha, {
  message: "As senhas não coincidem",
  path: ["confirmacaoSenha"],
});

/* ─── Agente ─── */

export const agenteCreateSchema = z.object({
  nome: nomeSchema,
  email: emailSchema,
  telefone: telefoneSchema,
  papel: z.enum(["gestor", "operador", "visualizador"], {
    errorMap: () => ({ message: "Selecione um papel válido" }),
  }),
});

export const agenteUpdateSchema = z.object({
  nome: nomeSchema.optional(),
  email: emailSchema.optional(),
  telefone: telefoneSchema,
});

/* ─── Ouvidoria ─── */

export const ouvidoriaCreateSchema = z.object({
  nome: trimmedString
    .min(2, "Nome da ouvidoria é obrigatório")
    .max(200, "Nome muito longo"),
  cnpj: trimmedString
    .regex(/^\d{14}$/, "CNPJ deve conter 14 dígitos numéricos")
    .optional()
    .or(z.literal("")),
  email: emailSchema.optional().or(z.literal("")),
  telefone: telefoneSchema,
  cidade: trimmedString.min(1, "Cidade é obrigatória"),
  estado: trimmedString.length(2, "UF deve ter 2 caracteres"),
  endereco: z.object({
    logradouro: trimmedString.min(1, "Logradouro é obrigatório"),
    numero: trimmedString.min(1, "Número é obrigatório"),
    bairro: trimmedString.min(1, "Bairro é obrigatório"),
    cidade: trimmedString.min(1, "Cidade é obrigatória"),
    estado: trimmedString.length(2, "UF deve ter 2 caracteres"),
    cep: trimmedString.regex(/^\d{8}$/, "CEP deve conter 8 dígitos numéricos"),
    pais: trimmedString.min(1, "País é obrigatório"),
  }).optional(),
});

/* ─── Solicitação / Comentário ─── */

export const comentarioSchema = z.object({
  conteudo: trimmedString
    .min(1, "O comentário não pode estar vazio")
    .max(2000, "Comentário muito longo (máx. 2000 caracteres)"),
  visivelCidadao: z.boolean().default(false),
});

export const observacaoInternaSchema = z.object({
  observacao: trimmedString
    .min(1, "A observação não pode estar vazia")
    .max(2000, "Observação muito longa (máx. 2000 caracteres)"),
});

/* ─── Reportar Problema ─── */

export const reportarProblemaSchema = z.object({
  problema: trimmedString
    .min(10, "Descreva o problema com pelo menos 10 caracteres")
    .max(2000, "Descrição muito longa (máx. 2000 caracteres)"),
  identificacaoTela: trimmedString.min(1, "Identificação da tela é obrigatória"),
});

/* ─── Perfil ─── */

export const perfilUpdateSchema = z.object({
  nome: nomeSchema.optional(),
  email: emailSchema.optional(),
  telefone: telefoneSchema,
});

/* ─── Utilitário de validação segura ─── */

export type ValidationResult<T> =
  | { success: true; data: T }
  | { success: false; errors: { path: string; message: string }[] };

export function safeParse<T>(schema: z.ZodSchema<T>, value: unknown): ValidationResult<T> {
  const result = schema.safeParse(value);
  if (result.success) {
    return { success: true, data: result.data };
  }
  return {
    success: false,
    errors: result.error.issues.map((issue) => ({
      path: issue.path.join("."),
      message: issue.message,
    })),
  };
}
